import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import JoditEditor from "jodit-react";
import "./UploadArticle.css";
import AdminNav from "../../../components/AdminNav/AdminNav";


export default function UploadArticle() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = location && location.state ? location.state : null;
  const [title, setTitle] = useState(initialState?.title || "");
  const [short_description, setShortDescription] = useState(initialState?.short_description || "");
  const [author, setAuthor] = useState(initialState?.author || "");
  const [content, setContent] = useState(initialState?.content || ""); // HTML from CKEditor
  const [tags, setTags] = useState(initialState?.tags || "");
  const [thumbnail, setThumbnail] = useState(initialState?.thumbnail || null);
  const [type, setType] = useState(initialState?.type || "");
  const [postTypes, setPostTypes] = useState([]);
  const [message, setMessage] = useState("");
  const [loading] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [thumbnailStatus, setThumbnailStatus] = useState('');
  const [compressing, setCompressing] = useState(false);
  const editor = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const storageUrl = process.env.REACT_APP_STORAGE_URL;


  useEffect(() => {
    // Fetch post types from backend
    fetch(`${apiUrl}/post_types`)
      .then(res => res.json())
      .then(data => setPostTypes(data))
      .catch(() => setPostTypes([]));
  }, [apiUrl]);

  useEffect(() => {
    if (!thumbnail) {
      setPreviewImageUrl(null);
      setThumbnailStatus('');
      return;
    }
    if (typeof thumbnail === 'object' && thumbnail instanceof File) {
      const url = URL.createObjectURL(thumbnail);
      setPreviewImageUrl(url);
      setThumbnailStatus(`${(thumbnail.size / 1024 / 1024).toFixed(2)} MB`);
      return () => {
        URL.revokeObjectURL(url);
        setPreviewImageUrl(null);
        setThumbnailStatus('');
      };
    }
    if (typeof thumbnail === 'string') {
      // If it's an absolute URL use it, otherwise assume backend storage path
      if (thumbnail.startsWith('http')) setPreviewImageUrl(thumbnail);
      else setPreviewImageUrl(`${storageUrl}${thumbnail}`);
    }
  }, [thumbnail, storageUrl]);

  async function compressImage(fileOrBlob, filename, targetBytes = 5000000) {
    if (!fileOrBlob) return null;
    try {
      const blob = fileOrBlob instanceof Blob ? fileOrBlob : new Blob([fileOrBlob]);
      const mime = blob.type || 'image/jpeg';
      const url = URL.createObjectURL(blob);
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (e) => reject(e);
        image.src = url;
      });

      const maxDim = 2000;
      let width = img.width;
      let height = img.height;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let quality = 0.92;
      const minQuality = 0.45;
      let attempt = 0;
      let resultBlob = null;
      let currentWidth = width;
      let currentHeight = height;

      while (attempt < 8) {
        canvas.width = currentWidth;
        canvas.height = currentHeight;
        ctx.clearRect(0, 0, currentWidth, currentHeight);
        ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
        // prefer jpeg for compression unless original is png and has transparency
        const outMime = mime === 'image/png' ? 'image/png' : 'image/jpeg';
        const currentQuality = quality;
        resultBlob = await new Promise((resolve) => canvas.toBlob(resolve, outMime, currentQuality));
        if (!resultBlob) break;
        if (resultBlob.size <= targetBytes) break;
        if (quality > minQuality) {
          quality = Math.max(minQuality, quality - 0.12);
        } else {
          currentWidth = Math.round(currentWidth * 0.8);
          currentHeight = Math.round(currentHeight * 0.8);
        }
        attempt++;
      }

      URL.revokeObjectURL(url);
      if (!resultBlob) return null;
      return new File([resultBlob], filename || 'thumbnail.jpg', { type: resultBlob.type || mime });
    } catch (e) {
      console.error('compressImage failed', e);
      return null;
    }
  }

  // Handle file input change: compress and validate size before accepting
  async function handleFileChange(file) {
    if (!file) {
      setThumbnail(null);
      return;
    }
    // Only images
    if (!file.type || !file.type.startsWith('image/')) {
      setMessage('Csak képfájl engedélyezett borítóképnek.');
      return;
    }
    const SERVER_LIMIT = 8388608; // 8MB
    const TARGET_BYTES = 5000000; // try to compress to ~5MB
    // If file already exceeds server limit, try compressing before even preview
    if (file.size >= SERVER_LIMIT) {
      setThumbnailStatus('Feldolgozás... (tömörítés próbálása)');
      setCompressing(true);
      const compressed = await compressImage(file, file.name, TARGET_BYTES);
      setCompressing(false);
      if (compressed && compressed.size < SERVER_LIMIT) {
        setThumbnail(compressed);
        setMessage('A borítókép automatikusan tömörítve lett a feltöltéshez.');
        return;
      }
      // compression failed or still too large
      setMessage('A kiválasztott borítókép túl nagy a szerver számára (8MB felett). Kérlek válassz kisebb képet.');
      setThumbnail(null);
      setThumbnailStatus('Túl nagy: ' + (file.size / 1024 / 1024).toFixed(2) + ' MB');
      return;
    }
    // If file is moderately large (e.g., >5MB) try to compress to reduce size
    if (file.size > TARGET_BYTES) {
      setThumbnailStatus('Feldolgozás... (tömörítés)');
      setCompressing(true);
      const compressed = await compressImage(file, file.name, TARGET_BYTES);
      setCompressing(false);
      if (compressed) {
        setThumbnail(compressed);
        setMessage('A borítókép automatikusan tömörítve lett a feltöltéshez.');
        return;
      }
      // if compression not possible, keep original but show size
      setThumbnail(file);
      setThumbnailStatus((file.size / 1024 / 1024).toFixed(2) + ' MB');
      return;
    }
    // Otherwise accept file as-is
    setThumbnail(file);
    setMessage('');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Instead of uploading immediately, navigate to Article page in preview mode with current data
    navigate('/article/preview', { state: { title, short_description, author, content, tags, thumbnail, type } });
  };

  const config = useMemo(() => ({
  height: 'auto',
  minHeight: 500,
  controls: {
    paragraph: {
      list: {
        p: 'Normál',
        h1: 'Címsor 1',
        h2: 'Címsor 2',
        h3: 'Tartalomjegyzék',
        h4: 'Címsor 4',
        h5: 'Címsor 5',
        h6: 'Címsor 6',
        blockquote: 'Idézet'
      }
    },
    classSpan: {
      // className -> label (Jodit expects keys as classes, values as labels)
      list: {
        toc: 'Tartalomjegyzék',
        lead: 'Bevezető szöveg',
        warning: 'Képaláírás',
        error: 'Link'
      }
    }
  },
  uploader: {
    insertImageAsBase64URI: false,
    url: `${apiUrl}/upload-image`,
    
    format: 'json',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      Accept: "application/json"
    },
    prepareData: function(data){
            data.append('file', this.file);
            return data;
        },
    isSuccess: function(resp){
        return !resp.error;
    },
    getMsg: function(resp){
        return resp.msg.join !== undefined ? resp.msg.join(' ') : resp.msg;
    },
    process: (resp) => {
      if (resp && resp.data && Array.isArray(resp.data.files)) {
        // Only keep valid, non-empty string URLs
        const urls = resp.data.files.filter(url => typeof url === 'string' && url.length > 0);
        return { files: urls };
      }
      return { files: [] };
    },
        defaultHandlerSuccess: function(data, resp){
            const files = data.files || [];
            if(files.length){
                this.selection.insertImage(files[0], null, 250);
            }
        },
        defaultHandlerError: function(resp){
            this.events.fire('errorPopap', this.i18n(resp.msg));
        }
  },
    buttons: [
      "font", "fontsize", "paragraph", "lineHeight", "classSpan", "spellcheck",
      "bold", "italic", "underline", "strikethrough", "superscript", "subscript", "|",
      "ul", "ol", "indent", "outdent", "|",
      "image", "video", "file", "table", "link", "cut", "copy", "paste", "selectall", "|",
      "align", "left", "center", "right", "justify", "undo", "redo", "hr", "eraser", "copyformat", "source", "preview", "find"
    ]
}), [apiUrl]);







  return (
    <div>
      <AdminNav />
      <div className="upload-article-container">
        <h2>Cikk feltöltése</h2>
        {/* Removed HTML modal button, CKEditor allows direct editing */}
        <form onSubmit={handleSubmit} className="upload-article-form">
          <label>Szerző:
            <input type="text" value={author} onChange={e => setAuthor(e.target.value)} required />
          </label>
          <label>Cím:
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} required />
          </label>
          <label>Alcím:
            <input type="text" value={short_description} onChange={e => setShortDescription(e.target.value)} placeholder="(nem kötelező)" />
          </label>
          <label>Típus:
            <select value={type} onChange={e => setType(e.target.value)} required>
              <option value="">Válassz típust</option>
              {postTypes.map(pt => (
                <option key={pt.id} value={pt.id}>{pt.type}</option>
              ))}
            </select>
          </label>
          <label>Tartalom:
            <JoditEditor
  ref={editor}
  value={content}
  config={config}
  onChange={newContent => setContent(newContent)}
  className="jodit-editor"
/>
          </label>
          <label>Címkék (vesszővel elválasztva):
            <input type="text" value={tags} onChange={e => setTags(e.target.value)} />
          </label>
          <label>Borítókép:
            <input type="file" accept="image/*" onChange={e => handleFileChange(e.target.files[0])} />
            {previewImageUrl && (
              <div style={{ marginTop: '10px' }}>
                <img
                  src={previewImageUrl}
                  alt="Thumbnail preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ccc' }}
                />
                <div style={{ fontSize: '0.9rem', color: '#555', marginTop: 6 }}>
                  Méret: {thumbnailStatus} {compressing && '(tömörítés folyamatban...)'}
                </div>
              </div>
            )}
          </label>
          <div style={{ marginTop: 12 }}>
            <button type="submit" className="form-submit" disabled={loading || compressing || (thumbnail && thumbnail.size >= 8388608)}>{loading ? "Feltöltés..." : "Feltöltés"}</button>
            {(thumbnail && thumbnail.size >= 8388608) && (
              <div style={{ color: 'crimson', marginTop: 8 }}>A kiválasztott kép túl nagy a szerver számára (8MB felett). Kérlek válassz kisebb képfájlt.</div>
            )}
            {compressing && (
              <div style={{ color: '#555', marginTop: 8 }}>A kép tömörítése folyamatban, kérlek várj...</div>
            )}
          </div>
        </form>
        {message && <div className="upload-article-message">{message}</div>}
        {/* Removed custom HTML modal and image insertion, CKEditor supports direct editing and image/media embed */}
      </div>
    </div>
  );
}
